import type {MedicalTransporterType, UserPermissionType} from '@ambler/prisma'
import {useAuth0} from '@auth0/auth0-react'
import {gql} from '@apollo/client'
import {useMutation} from '../../hooks/use-mutation'
import {getUpdateDevicePayload} from '../../lib/firebase-messaging'
import type {BO_logoutDeviceMutation_, BO_logoutDeviceMutationVariables} from './auth.generated'
import {useProfile} from './profile'

type SignInOptions = {
  callbackUrl?: string
}

type MtAcl = {
  canRead: boolean
  canWrite: boolean
  mt: MedicalTransporter
}

const logoutDeviceMutation = gql`
  mutation BO_logoutDeviceMutation($data: PushNotificationsUpsertDeviceDataInput!) {
    pushNotificationsUpsertDevice(data: $data)
  }
`

export const useAuth = (): {
  user: {
    id: string
    auth0Id: string
    name: string
    email: string
    role: string
  }
  mtAcls: MtAcl[]
  isAuthenticated: boolean
  isEmailConfirmed: boolean
  isLoading: boolean
  isConnectAs: boolean
  signIn: (arg0?: SignInOptions) => void
  signOut: (redirectToHome?: boolean) => void
  permissions: {id: string; type: UserPermissionType}[]
} => {
  const [logoutDevice] = useMutation<BO_logoutDeviceMutation_, BO_logoutDeviceMutationVariables>({
    mutation: logoutDeviceMutation,
  })

  const {user, isAuthenticated, isLoading, loginWithRedirect, logout} = useAuth0()

  const userProfile = user?.['https://api.ambler.fr/']
  const {connectAs, role, id, name, email, auth0Id, isEmailConfirmed, permissions} = userProfile || {}

  const isConnectAs = Boolean(connectAs)
  const {mts, isLoading: isProfileLoading} = useProfile()

  return {
    user: {
      id,
      auth0Id,
      name,
      email,
      role,
    },
    mtAcls: mts.map(mtAcl => ({
      mt: mtAcl.mt,
      canRead: mtAcl.acl.canRead,
      canWrite: mtAcl.acl.canWrite,
    })),
    isAuthenticated,
    isEmailConfirmed,
    permissions,
    isLoading: isLoading || isProfileLoading,
    isConnectAs,
    signIn: (options: SignInOptions = {}) => {
      const {callbackUrl} = options
      const params = {
        redirectUri: callbackUrl ? callbackUrl : window.location.origin,
      }
      return loginWithRedirect(params)
    },
    signOut: async () => {
      try {
        await logoutDevice({
          variables: {
            data: {
              ...(await getUpdateDevicePayload()),
              state: 'LOGGED_OUT',
              fcmToken: null,
            },
          },
        })
      } catch (_err) {
        // no op but we want the logout to happen
      }
      return logout({returnTo: window.location.origin})
    },
  }
}

type MedicalTransporter = {
  id: string
  name?: string
  type: MedicalTransporterType
}

type MainMt = {
  canRead: boolean
  canWrite: boolean
  mt: MedicalTransporter
}

export const useMainMt = (): MainMt => {
  const {isLoading, isAuthenticated, mtAcls} = useAuth()

  if (isLoading || !isAuthenticated) {
    return null
  }

  return mtAcls?.[0]
}
